import { useState, useEffect } from "react";
import { WordSelectionItem } from "../../types/game";
import "./MatchingMode.css";
import { GamePanelProps } from "../ui/GameContent";
import { shuffle } from "../../utils/utils";
import useGameStore from "../../context/GameContext";
import ConfirmationModal from "../ui/ConfirmationModal";
import { CompletionModal } from "../ui/CompletionModal";

export const MatchingMode = ({ items, libraryName }: GamePanelProps) => {
  const { pauseTimer, getFormattedTime, gameId } = useGameStore();

  const [shuffledWords, setShuffledWords] = useState<string[]>([]);
  const [shuffledAnswers, setShuffledAnswers] = useState<string[]>([]);
  const [selectedCard, setSelectedCard] = useState<{
    type: "word" | "answer";
    value: string;
  } | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<string[]>([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionTime, setCompletionTime] = useState(0);

  // 初始化游戏
  const initGame = () => {
    const shuffled = shuffle([...items]);
    setShuffledWords(shuffled.map((item) => item.word));
    setShuffledAnswers(shuffle(shuffled.map((item) => item.correctAnswer)));
    setSelectedCard(null);
    setMatchedPairs([]);
    setShowCompletionModal(false);
  };

  useEffect(() => {
    initGame();
  }, [items, gameId]);

  // 处理卡片点击
  const handleCardClick = (type: "word" | "answer", value: string) => {
    // 如果卡片已匹配，直接返回
    if (matchedPairs.includes(value)) return;

    // 如果点击的卡片与已选中的卡片属于同一列，颤抖提示无效点击
    if (selectedCard?.type === type) {
      const cardElement = document.querySelector(`[data-value="${value}"]`);
      if (cardElement) {
        cardElement.classList.add("shake");
        setTimeout(() => cardElement.classList.remove("shake"), 500);
      }
      return;
    }

    // 如果没有选中的卡片，则标记当前卡片为选中
    if (!selectedCard) {
      setSelectedCard({ type, value });
      return;
    }

    // 如果选中的卡片和当前点击的卡片属于不同列，检查是否匹配
    const selectedValue = selectedCard.value;
    const isMatch = items.some(
      (item) =>
        (item.word === selectedValue && item.correctAnswer === value) ||
        (item.word === value && item.correctAnswer === selectedValue)
    );

    if (isMatch) {
      // 匹配成功，标记为已匹配
      setMatchedPairs((prev) => [...prev, selectedValue, value]);
      setSelectedCard(null);

      // 检查是否完成所有匹配
      if (matchedPairs.length + 2 === items.length * 2) {
        pauseTimer();
        const timeStr = getFormattedTime();
        const timeParts = timeStr.split(":");
        let totalSeconds = 0;

        if (timeParts.length === 2) {
          totalSeconds = parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]);
        } else if (timeParts.length === 3) {
          totalSeconds =
            parseInt(timeParts[0]) * 3600 +
            parseInt(timeParts[1]) * 60 +
            parseInt(timeParts[2]);
        }

        setCompletionTime(totalSeconds);
        setShowCompletionModal(true);
      }
    } else {
      // 匹配失败，提示错误并闪烁红色
      const selectedElement = document.querySelector(
        `[data-value="${selectedValue}"]`
      );
      const currentElement = document.querySelector(`[data-value="${value}"]`);

      if (selectedElement) {
        selectedElement.classList.add("flash-red");
        setTimeout(() => selectedElement.classList.remove("flash-red"), 500);
      }
      if (currentElement) {
        currentElement.classList.add("flash-red");
        setTimeout(() => currentElement.classList.remove("flash-red"), 500);
      }

      // 取消选择
      setSelectedCard(null);
    }
  };

  // 重置游戏
  const handlePlayAgain = () => {
    initGame();
    useGameStore.getState().resetTimer();
    useGameStore.getState().startTimer();
  };

  return (
    <div className="matching-mode-container">
      {/* 左侧单词列 */}
      <div className="column words-column">
        {shuffledWords.map((word) => (
          <div
            key={word}
            className={`card ${matchedPairs.includes(word) ? "matched" : ""} ${
              selectedCard?.value === word ? "selected" : ""
            }`}
            data-value={word}
            onClick={() => handleCardClick("word", word)}
          >
            {word}
          </div>
        ))}
      </div>

      {/* 右侧答案列 */}
      <div className="column answers-column">
        {shuffledAnswers.map((answer) => (
          <div
            key={answer}
            className={`card ${
              matchedPairs.includes(answer) ? "matched" : ""
            } ${selectedCard?.value === answer ? "selected" : ""}`}
            data-value={answer}
            onClick={() => handleCardClick("answer", answer)}
          >
            {answer}
          </div>
        ))}
      </div>

      {/* 完成后的模态框 */}
      {showCompletionModal && (
        <CompletionModal
          time={completionTime}
          missedWords={[]} // 匹配模式没有错题，传递空数组
          onClose={() => setShowCompletionModal(false)}
          onPlayAgain={handlePlayAgain}
          mode="matching" // 指定模式为 matching
        />
      )}
    </div>
  );
};
